import type { ReactNode } from "react";
import { useRouteMatch } from "react-router-dom";
import { BrowserNotificationPrompt } from "@/react/components/Layout/BrowserNotificationPrompt";
import { MobileBottomToolbar } from "@/react/components/Layout/MobileBottomToolbar";
import { Body } from "@/react/components/Layout/StandardLayoutV2/Body";
import { Breadcrumb } from "@/react/components/Layout/StandardLayoutV2/Breadcrumb";
import { Content } from "@/react/components/Layout/StandardLayoutV2/Content";
import { CoverImage } from "@/react/components/Layout/StandardLayoutV2/CoverImage";
import { Header } from "@/react/components/Layout/StandardLayoutV2/Header";
import { Right } from "@/react/components/Layout/StandardLayoutV2/Right";
import { SideBar } from "@/react/components/Spaces/Sidebar";
import { isSafariMontereyOrOlder } from "@/react/helpers/browserHelpers";
import {
  isChatSpace,
  isGridOrMasonryViewImageSpace,
} from "@/react/helpers/spaceHelpers";
import { usePostListPinnedOnSidebar } from "@/react/hooks/posts/usePostList";
import { useAppendBodyClasses } from "@/react/hooks/useAppendBodyClasses";
import { useIsConnectEnabled } from "@/react/hooks/useIsConnectEnabled";
import { useRouterQueryParams } from "@/react/hooks/useRouterQueryParams";
import { useSpaceSlugFromUrl } from "@/react/hooks/useSpaceSlugFromUrl";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import { AppBanner } from "@circle-react/components/AppBanner";
import { useSettingsHeader } from "@circle-react/components/shared/uikit/HeaderV3/useSettingsHeader";
import { usePunditUserContext, useSpacesContext } from "@circle-react/contexts";
import { useLayoutPartialsContext } from "@circle-react/contexts/layoutPartialsContext";
import {
  isPromoCodeEnabled,
  isSubscriptionExpired,
} from "@circle-react/helpers/communityHelpers";
import { shouldRenderBillingAfterCheckout } from "@circle-react/helpers/plansHelpers";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { useMasqueradeEnabledAndActive } from "@circle-react/hooks/useMasqueradeEnabledAndActive";
import { AppBar } from "../AppBar";
import { MobileDrawer } from "../MobileDrawer";
import { CommunityContent } from "./CommunityContent";
import { CommunityWrapper } from "./CommunityWrapper";
import { ContentWrapper } from "./ContentWrapper";
import { PlanTrialTopBanner } from "./PlanTrialTopBanner";
import { RailbarContainer } from "./RailbarContainer";
import { SidebarWrapper } from "./SidebarWrapper";
import { StandardLayoutV2Wrapper } from "./StandardLayoutV2Wrapper";
import { TrialBannerV2 } from "./TrialBannerV2";
import { shouldDisableRightSidebar } from "./shouldDisableRightSidebar";
import { useShouldShowMobileBottomBar } from "./useMobileBottomBar";
import { useTopBarClassNames } from "./useTopBarClassNames";
import "./overrides.scss";
import "./styles.scss";

interface RouteParams {
  postSlug?: string;
}

export interface StandardLayoutV2Props {
  bodyClassNames?: string;
  children: ReactNode;
  disableRightSidebar?: boolean;
  disableScroll?: boolean;
  disableSidebar?: boolean;
  hideAppBar?: boolean;
  id?: string;
  renderLeftSidebar?: () => ReactNode;
  shouldHideSidebarNestedNavBar?: boolean;
}

const StandardLayoutV2Root = ({
  bodyClassNames = "",
  children,
  disableRightSidebar = false,
  disableScroll = false,
  disableSidebar = false,
  hideAppBar = false,
  shouldHideSidebarNestedNavBar = false,
  id,
  renderLeftSidebar,
}: StandardLayoutV2Props) => {
  const { currentCommunityMember, currentCommunity } = usePunditUserContext();
  const { posts: pinnedPosts } = usePostListPinnedOnSidebar();
  const containerProps: any = { id };
  const partialsContext: any = useLayoutPartialsContext();
  const mobile_drawer_html = partialsContext?.data?.mobile_drawer_html;
  const isMasqueradingEnabledAndActive = useMasqueradeEnabledAndActive();
  const { isElectron } = useElectronAPI();

  const { isSettingsRoute, isReactFlowWorkflowsRoute } = useSettingsHeader();
  const shouldShowMobileBottomToolbar = useShouldShowMobileBottomBar();

  const isDvhUnsupportedInSafari = isSafariMontereyOrOlder();

  if (!disableSidebar && mobile_drawer_html) {
    containerProps["data-controller"] = "mobile-sidebar";
  }

  useAppendBodyClasses(
    classNames(
      "is-standard-layout-v2 react-standard-layout bg-secondary is-standard-layout-v2--v3-enabled",
      bodyClassNames,
      {
        "is-standard-layout-v2--v3-enabled--hidden-nested-links":
          shouldHideSidebarNestedNavBar,
        "safari-dvh-unsupported": isDvhUnsupportedInSafari,
      },
    ),
  );
  const queryParams = useRouterQueryParams();

  const match = useRouteMatch<RouteParams>("/c/:spaceSlug/:postSlug");
  const postSlug = match?.params?.postSlug;

  const isHomePage = useRouteMatch("/home")?.isExact;
  const isFeedPage = useRouteMatch("/feed")?.isExact;
  const isMemberDirectoryPage = useRouteMatch("/members");
  const isConnectEnabled = useIsConnectEnabled();

  const spaceSlug = useSpaceSlugFromUrl();
  const spaceData = useSpacesContext().helpers.findBy({ slug: spaceSlug });
  const space = spaceData || {};

  const hasSubscriptionExpired =
    isSubscriptionExpired(currentCommunity) &&
    !shouldRenderBillingAfterCheckout(queryParams);
  const isPromoCodeEnabledForCommunity = isPromoCodeEnabled(currentCommunity);

  // TODO: refactor so that we rely on child props to set this value.
  const disableLayoutRightSidebar = shouldDisableRightSidebar({
    disableRightSidebar,
    postSlug,
    space,
    pinnedPosts,
    isHomePage: isHomePage || isFeedPage,
    currentCommunityMember,
  });

  const isBgPrimarySpace =
    isChatSpace(space) ||
    isGridOrMasonryViewImageSpace(space) ||
    isPromoCodeEnabledForCommunity ||
    (isMemberDirectoryPage && isConnectEnabled);

  const isPromoCodeEnabledAndHasSubscriptionExpired =
    isPromoCodeEnabledForCommunity && hasSubscriptionExpired;

  const topBarClassNames = useTopBarClassNames(
    isPromoCodeEnabledAndHasSubscriptionExpired,
    isMasqueradingEnabledAndActive,
  );

  return (
    <CommunityWrapper {...containerProps}>
      <div data-testid="mobile-drawer-wrapper">
        {!disableSidebar && <MobileDrawer />}
      </div>
      <div
        className={classNames(
          "bg-secondary flex w-full flex-col",
          topBarClassNames,
          {
            "h-screen": !disableScroll,
            "h-full": disableScroll,
            // small workaround to stop cropping content when in Electron and in Settings route
            "!h-[calc(100vh-105px)]": isElectron && isSettingsRoute,
          },
        )}
        data-testid="standard-layout-v2"
      >
        <PlanTrialTopBanner />
        <TrialBannerV2 />
        <AppBanner />
        <CommunityContent disableSidebar={disableSidebar}>
          {!hideAppBar && <AppBar />}
          <StandardLayoutV2Wrapper
            disableSidebar={disableSidebar}
            disableLayoutRightSidebar={disableLayoutRightSidebar}
            hasSettingsSidebar={
              !isReactFlowWorkflowsRoute &&
              isSettingsRoute &&
              !hasSubscriptionExpired &&
              !isPromoCodeEnabledForCommunity
            }
            disableSidebarNestedBarLinks={shouldHideSidebarNestedNavBar}
          >
            {!disableSidebar && !isReactFlowWorkflowsRoute && (
              <SidebarWrapper>
                {renderLeftSidebar ? renderLeftSidebar() : <SideBar />}
              </SidebarWrapper>
            )}
            <ContentWrapper
              isBgSecondary={!isBgPrimarySpace}
              isBgPrimary={!!isBgPrimarySpace}
            >
              {children}
            </ContentWrapper>
            <div className="standard-layout-v2__railbar">
              <RailbarContainer />
            </div>
            <BrowserNotificationPrompt />
            <div id="mobile-dropdown-container" />
            {shouldShowMobileBottomToolbar && <MobileBottomToolbar />}
          </StandardLayoutV2Wrapper>
        </CommunityContent>
      </div>
    </CommunityWrapper>
  );
};

export const StandardLayoutV2 = Object.assign(StandardLayoutV2Root, {
  Header,
  Breadcrumb,
  Content,
  Body,
  Right,
  CoverImage,
});
